import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet, TrendingUp, Clock, DollarSign, Calendar, BarChart3,
  Heart, Crown, Store, AlertCircle, CheckCircle,
  ArrowDownLeft, Banknote, Filter, CreditCard, Building2, ExternalLink, Loader2,
  Unlink, RefreshCw, Eye, EyeOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { toast } from 'sonner';
import { financialsApi, creatorPayoutApi } from '../lib/api';

const SOURCE_COLORS = {
  platform_revenue: '#8B5CF6',
  ppv: '#F59E0B',
  merchandise: '#00FF9D',
  donations: '#EC4899',
};

const SOURCE_ICONS = {
  donation: Heart,
  ppv: Crown,
  merchandise: Store,
  payout: ArrowDownLeft,
  platform_revenue: Building2,
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0F0B1E] border border-[#8B5CF6]/30 rounded-lg px-4 py-3 shadow-xl">
      <p className="text-sm text-gray-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: p.color }}>
          {p.name}: ${p.value.toFixed(2)}
        </p>
      ))}
    </div>
  );
};

const FinancialsDashboard = () => {
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all_time');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Stripe Connect state
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);

  // Stripe disconnect/replace state
  const [stripeAction, setStripeAction] = useState(null); // 'disconnect' or 'replace'
  const [stripePassword, setStripePassword] = useState('');
  const [stripeActionLoading, setStripeActionLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Chart series toggle state
  const [chartSeries, setChartSeries] = useState({
    platform_revenue: true,
    ppv: true,
    merchandise: true,
    donations: true,
  });

  const fetchData = useCallback(async (p, start, end) => {
    try {
      setLoading(true);
      const data = await financialsApi.get(p, start, end);
      setFinancials(data);
    } catch (e) {
      console.error('Financials load error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStripeStatus = useCallback(async () => {
    try {
      const status = await creatorPayoutApi.getStripeAccountStatus();
      setStripeAccountStatus(status);
    } catch (e) {
      console.error('Stripe status error:', e);
    }
  }, []);

  useEffect(() => {
    fetchData(period);
    fetchStripeStatus();
  }, [fetchData, fetchStripeStatus, period]);

  const handlePeriodChange = (val) => {
    setPeriod(val);
    if (val !== 'custom') fetchData(val);
  };

  const applyCustomRange = () => {
    if (!customStart || !customEnd) { toast.error('Select start and end dates'); return; }
    try {
      fetchData('custom', new Date(customStart).toISOString(), new Date(customEnd + 'T23:59:59').toISOString());
    } catch {
      toast.error('Invalid date range');
    }
  };

  const handleStripeConnect = async () => {
    setConnectingStripe(true);
    try {
      const result = await creatorPayoutApi.initiateStripeConnect(window.location.origin);
      if (result.url) {
        window.location.href = result.url;
      } else {
        toast.error('Could not start Stripe onboarding');
      }
    } catch (e) {
      toast.error(e.message || 'Stripe Connect failed');
    } finally { setConnectingStripe(false); }
  };

  const handleStripeAction = async () => {
    if (!stripePassword) { toast.error('Password required'); return; }
    setStripeActionLoading(true);
    try {
      if (stripeAction === 'disconnect') {
        await creatorPayoutApi.disconnectStripe(stripePassword);
        toast.success('Stripe account disconnected');
      } else if (stripeAction === 'replace') {
        const data = await creatorPayoutApi.replaceStripe(stripePassword, window.location.origin);
        if (data.url) {
          window.location.href = data.url;
          return;
        }
      }
      setStripeAction(null);
      setStripePassword('');
      setShowPassword(false);
      fetchStripeStatus();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally { setStripeActionLoading(false); }
  };

  if (loading && !financials) {
    return (
      <div className="admin-card p-12 text-center" data-testid="financials-loading">
        <Wallet className="w-16 h-16 mx-auto text-gray-600 mb-4 animate-pulse" />
        <p className="text-gray-400">Loading financial data...</p>
      </div>
    );
  }

  if (!financials) return null;

  const pieData = Object.entries(financials.breakdown)
    .filter(([, v]) => v.amount > 0)
    .map(([key, v]) => ({ name: v.label, value: v.amount, key }));

  return (
    <div className="space-y-6" data-testid="financials-dashboard">

      {/* Period Selector */}
      <div className="admin-card p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="period-selector">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-400">Period:</span>
        </div>
        <Select value={period} onValueChange={handlePeriodChange}>
          <SelectTrigger className="w-40 bg-[#030014] border-[#8B5CF6]/20 h-9" data-testid="period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0F0B1E] border-[#8B5CF6]/20">
            <SelectItem value="all_time">All time</SelectItem>
            <SelectItem value="daily">Today</SelectItem>
            <SelectItem value="weekly">Last 7 days</SelectItem>
            <SelectItem value="monthly">Last 30 days</SelectItem>
            <SelectItem value="custom">Custom</SelectItem>
          </SelectContent>
        </Select>
        {period === 'custom' && (
          <div className="flex items-center gap-2">
            <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              className="bg-[#030014] border-[#8B5CF6]/20 h-9 w-40" data-testid="custom-start-date" />
            <span className="text-gray-500">to</span>
            <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              className="bg-[#030014] border-[#8B5CF6]/20 h-9 w-40" data-testid="custom-end-date" />
            <Button size="sm" onClick={applyCustomRange} className="bg-[#8B5CF6] hover:bg-[#7C3AED] h-9" data-testid="apply-range-btn">Apply</Button>
          </div>
        )}
      </div>

      {/* Source Breakdown Cards (Revenue by Source) — TOP POSITION */}
      <div className="admin-card p-6" data-testid="source-breakdown">
        <h3 className="text-lg font-semibold mb-5 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#8B5CF6]" /> Revenue by Source
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(financials.breakdown).map(([key, val]) => {
            const Icon = SOURCE_ICONS[key] || DollarSign;
            const color = SOURCE_COLORS[key] || '#8B5CF6';
            return (
              <div key={key} className="p-4 rounded-xl border border-white/5 bg-[#030014] hover:border-opacity-40 transition-colors"
                style={{ borderColor: `${color}33` }} data-testid={`source-${key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4" style={{ color }} />
                  <span className="text-xs font-medium text-gray-300">{val.label}</span>
                </div>
                <p className="text-xl font-bold" style={{ color }}>${val.amount.toFixed(2)}</p>
                {val.count !== undefined && (
                  <p className="text-[11px] text-gray-500 mt-1">{val.count} transactions</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Revenue Chart + Breakdown */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 admin-card p-6" data-testid="revenue-chart">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#8B5CF6]" /> Revenue Over Time
          </h3>
          {/* Toggle buttons for chart series */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="chart-toggles">
            {[
              { key: 'platform_revenue', label: 'Platform Revenue', color: SOURCE_COLORS.platform_revenue },
              { key: 'ppv', label: 'Pay-per-View', color: SOURCE_COLORS.ppv },
              { key: 'merchandise', label: 'Merchandise', color: SOURCE_COLORS.merchandise },
              { key: 'donations', label: 'Donations', color: SOURCE_COLORS.donations },
            ].map(s => (
              <button
                key={s.key}
                onClick={() => setChartSeries(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all border ${chartSeries[s.key] ? 'opacity-100' : 'opacity-40'}`}
                style={{
                  borderColor: s.color + '60',
                  backgroundColor: chartSeries[s.key] ? s.color + '20' : 'transparent',
                  color: s.color,
                }}
                data-testid={`toggle-${s.key}`}
              >
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                {s.label}
              </button>
            ))}
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={financials.chart}>
                <defs>
                  <linearGradient id="grad_platform_revenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SOURCE_COLORS.platform_revenue} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={SOURCE_COLORS.platform_revenue} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="grad_ppv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SOURCE_COLORS.ppv} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={SOURCE_COLORS.ppv} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="grad_merchandise" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SOURCE_COLORS.merchandise} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={SOURCE_COLORS.merchandise} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="grad_donations" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={SOURCE_COLORS.donations} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={SOURCE_COLORS.donations} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1A1033" />
                <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={v => `$${v}`} />
                <Tooltip content={<CustomTooltip />} />
                {chartSeries.platform_revenue && (
                  <Area type="monotone" dataKey="platform_revenue" name="Platform Revenue" stroke={SOURCE_COLORS.platform_revenue} fill="url(#grad_platform_revenue)" strokeWidth={2} />
                )}
                {chartSeries.ppv && (
                  <Area type="monotone" dataKey="ppv" name="Pay-per-View" stroke={SOURCE_COLORS.ppv} fill="url(#grad_ppv)" strokeWidth={2} />
                )}
                {chartSeries.merchandise && (
                  <Area type="monotone" dataKey="merchandise" name="Merchandise" stroke={SOURCE_COLORS.merchandise} fill="url(#grad_merchandise)" strokeWidth={2} />
                )}
                {chartSeries.donations && (
                  <Area type="monotone" dataKey="donations" name="Donations" stroke={SOURCE_COLORS.donations} fill="url(#grad_donations)" strokeWidth={2} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="admin-card p-6" data-testid="revenue-breakdown-pie">
          <h3 className="text-lg font-semibold mb-4">Revenue Mix</h3>
          {pieData.length > 0 ? (
            <>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={4} dataKey="value">
                      {pieData.map((entry) => (
                        <Cell key={entry.key} fill={SOURCE_COLORS[entry.key]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(val) => `$${val.toFixed(2)}`}
                      contentStyle={{ backgroundColor: '#0F0B1E', border: '1px solid rgba(139,92,246,0.3)', borderRadius: '8px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 mt-2">
                {pieData.map((item) => (
                  <div key={item.key} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SOURCE_COLORS[item.key] }} />
                      <span className="text-gray-300">{item.name}</span>
                    </div>
                    <span className="font-medium">${item.value.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <BarChart3 className="w-10 h-10 text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No revenue in this period</p>
            </div>
          )}
        </div>
      </div>

      {/* Automatic Payout Banner */}
      <div className="admin-card p-6 border-[#00FF9D]/20" data-testid="payout-info">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-1">
              <Banknote className="w-5 h-5 text-[#00FF9D]" /> Automatic Monthly Payout
            </h3>
            <p className="text-sm text-gray-400">
              Next payout on <span className="text-white font-medium">{financials.payout.next_date}</span> — revenue from <span className="text-white font-medium">{financials.payout.revenue_month || 'N/A'}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            {financials.payout.eligible ? (
              <span className="bg-[#00FF9D]/20 text-[#00FF9D] border border-[#00FF9D]/30 px-3 py-1 text-sm rounded-full inline-flex items-center">
                <CheckCircle className="w-3.5 h-3.5 mr-1" /> Eligible
              </span>
            ) : (
              <span className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-3 py-1 text-sm rounded-full">
                Below ${financials.payout.min_threshold} min
              </span>
            )}
            {financials.payout.payment_connected ? (
              <span className="bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/20 px-3 py-1 text-sm rounded-full inline-flex items-center">
                <CreditCard className="w-3.5 h-3.5 mr-1" /> Connected
              </span>
            ) : (
              <span className="bg-red-500/20 text-red-400 border border-red-500/30 px-3 py-1 text-sm rounded-full">
                No Payment Method
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Payout Methods Section — Stripe Only */}
      <div className="admin-card p-6" data-testid="payout-methods-section">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#8B5CF6]" /> Payout Method
          </h3>
          {stripeAccountStatus?.connected && (
            <Button
              variant="outline"
              size="sm"
              className="border-[#8B5CF6]/30 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 hover:text-[#A78BFA] h-8 text-xs"
              data-testid="stripe-dashboard-btn"
              onClick={async () => {
                try {
                  const data = await creatorPayoutApi.getStripeDashboardLink();
                  window.open(data.url, '_blank');
                } catch (e) {
                  toast.error(e.message || 'Could not open Stripe Dashboard');
                }
              }}
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Stripe Dashboard
            </Button>
          )}
        </div>

        <div className="space-y-4 p-4 rounded-xl bg-[#030014] border border-white/5" data-testid="stripe-form">
          {stripeAccountStatus?.connected ? (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#00FF9D]/10 border border-[#00FF9D]/20">
              <CheckCircle className="w-5 h-5 text-[#00FF9D] flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#00FF9D]">Stripe Account Connected</p>
                <p className="text-xs text-gray-400">
                  {stripeAccountStatus.email && `${stripeAccountStatus.email} · `}
                  Payouts {stripeAccountStatus.payouts_enabled ? 'enabled' : 'pending'}
                </p>
              </div>
            </div>
          ) : stripeAccountStatus?.details_submitted ? (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-400">Verification In Progress</p>
                <p className="text-xs text-gray-400">Stripe is verifying your account details. This may take 1-2 business days.</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <CreditCard className="w-10 h-10 text-[#8B5CF6] mx-auto mb-3" />
              <p className="text-sm text-gray-300 mb-1">Set up your payout method</p>
              <p className="text-xs text-gray-500 mb-4">Stripe handles all payments, tax reporting, and compliance</p>
            </div>
          )}
          <Button onClick={handleStripeConnect} disabled={connectingStripe}
            className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] text-white" data-testid="stripe-connect-btn">
            {connectingStripe ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</>
            ) : stripeAccountStatus?.connected ? (
              <><ExternalLink className="w-4 h-4 mr-2" /> Update Stripe Account</>
            ) : (
              <><ExternalLink className="w-4 h-4 mr-2" /> Connect with Stripe</>
            )}
          </Button>
          {stripeAccountStatus?.connected && (
            <div className="flex gap-2 mt-2">
              <Button variant="outline" onClick={() => setStripeAction('replace')}
                className="flex-1 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 hover:text-yellow-300"
                data-testid="stripe-replace-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Replace Account
              </Button>
              <Button variant="outline" onClick={() => setStripeAction('disconnect')}
                className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                data-testid="stripe-disconnect-btn">
                <Unlink className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            </div>
          )}
        </div>

        {/* Stripe Action Password Confirmation Dialog */}
        <Dialog open={!!stripeAction} onOpenChange={(open) => { if (!open) { setStripeAction(null); setStripePassword(''); setShowPassword(false); } }}>
          <DialogContent className="bg-[#0F0B1E] border border-white/10 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white text-base">
                {stripeAction === 'disconnect' ? 'Disconnect Stripe Account' : 'Replace Stripe Account'}
              </DialogTitle>
              <DialogDescription className="text-gray-400 text-sm">
                {stripeAction === 'disconnect'
                  ? 'Your accumulated revenue will be paid to your next connected account. Until you reconnect, donations, merchandise sales and PPV purchases will be unavailable.'
                  : 'Your current Stripe account will be disconnected and you will be redirected to connect a new one. All accumulated revenue will be transferred to the new account.'}
              </DialogDescription>
            </DialogHeader>
            <div className="py-3">
              <label className="text-xs text-gray-400 mb-1 block">Confirm your password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={stripePassword}
                  onChange={(e) => setStripePassword(e.target.value)}
                  placeholder="Enter your password"
                  className="bg-[#030014] border-white/10 text-white pr-10"
                  data-testid="stripe-action-password"
                  onKeyDown={(e) => e.key === 'Enter' && handleStripeAction()}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button variant="ghost" className="text-gray-400 hover:text-white"
                onClick={() => { setStripeAction(null); setStripePassword(''); setShowPassword(false); }}
                data-testid="stripe-action-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleStripeAction}
                disabled={stripeActionLoading || !stripePassword}
                className={stripeAction === 'disconnect'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-yellow-600 hover:bg-yellow-700 text-white'}
                data-testid="stripe-action-confirm">
                {stripeActionLoading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                  : stripeAction === 'disconnect' ? 'Disconnect' : 'Replace Account'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {!financials.payout.payment_connected && !stripeAccountStatus?.connected && (
          <div className="mt-4 p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-lg" data-testid="connect-payment-prompt">
            <p className="text-sm text-yellow-300 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Connect your Stripe account to enable automatic payouts
            </p>
          </div>
        )}
      </div>

      {/* Transaction List */}
      <div className="admin-card p-6" data-testid="transaction-list">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-400" /> Transactions
        </h3>
        {financials.transactions.length > 0 ? (
          <div className="space-y-2">
            {financials.transactions.map((txn, i) => {
              const Icon = SOURCE_ICONS[txn.type] || DollarSign;
              const isPayout = txn.type === 'payout';
              return (
                <div key={txn.id || i}
                  className="flex items-center gap-4 p-3 rounded-lg bg-[#030014] border border-white/5 hover:border-[#8B5CF6]/20 transition-colors"
                  data-testid={`txn-${txn.id || i}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isPayout ? 'bg-red-500/10' : 'bg-[#8B5CF6]/10'}`}>
                    <Icon className={`w-4 h-4 ${isPayout ? 'text-red-400' : 'text-[#8B5CF6]'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{txn.description}</p>
                    <p className="text-[11px] text-gray-500">
                      {new Date(txn.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
                      txn.source === 'Donation' ? 'bg-pink-500/15 text-pink-400' :
                      txn.source === 'Pay-Per-View' ? 'bg-yellow-500/15 text-yellow-400' :
                      txn.source === 'Merchandise' ? 'bg-[#00FF9D]/15 text-[#00FF9D]' :
                      txn.source === 'Payout' ? 'bg-red-500/15 text-red-400' :
                      'bg-[#8B5CF6]/15 text-[#8B5CF6]'
                    }`} data-testid={`txn-source-${txn.id || i}`}>
                      {txn.source}
                    </span>
                    <span className={`text-sm font-bold tabular-nums ${txn.amount >= 0 ? 'text-[#00FF9D]' : 'text-red-400'}`}>
                      {txn.amount >= 0 ? '+' : ''}${Math.abs(txn.amount).toFixed(2)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-10">
            <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No transactions in this period</p>
            <p className="text-xs text-gray-500 mt-1">Revenue will appear here as it comes in</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialsDashboard;
